import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useSuperAdmin } from '../../lib/auth';
import Logo from '../../components/Logo';

export default function SuperAdminLogin() {
    const nav = useNavigate();
    const { signIn, superAdmin } = useSuperAdmin();
    const [step, setStep] = useState('creds'); // 'creds' | 'totp'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [totpCode, setTotpCode] = useState('');
    const [challenge, setChallenge] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    React.useEffect(() => { if (superAdmin) nav('/', { replace: true }); }, [superAdmin, nav]);

    const step1 = async () => {
        setError(''); setBusy(true);
        try {
            const r = await api.post('/superadmin/auth/login-step1', { email: email.trim().toLowerCase(), password });
            if (r.data.stage === 'logged_in') {
                // First-time or TOTP-disabled login — token issued directly.
                signIn(r.data.access_token, r.data.super_admin);
                nav('/', { replace: true });
            } else if (r.data.stage === 'totp_required') {
                setChallenge(r.data.challenge_token);
                setStep('totp');
            }
        } catch (e) { setError(e.response?.data?.detail || 'Login failed'); }
        finally { setBusy(false); }
    };
    const step2 = async () => {
        setError(''); setBusy(true);
        try {
            const r = await api.post('/superadmin/auth/login-step2-totp', { challenge_token: challenge, totp_code: totpCode.trim() });
            signIn(r.data.access_token, r.data.super_admin); nav('/', { replace: true });
        } catch (e) { setError(e.response?.data?.detail || 'Verification failed'); }
        finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-sdf-ivory flex flex-col">
            <div className="px-4 py-8 border-b border-sdf-line flex items-center justify-center"><Logo variant="login" /></div>
            <main className="flex-1 flex items-start justify-center px-4 pt-10">
                <div className="w-full max-w-md">
                    <h1 className="text-2xl font-extrabold">Super Admin Sign In</h1>
                    <p className="mt-1 text-sm text-sdf-text-2">Enter your email and password to continue.</p>

                    {step === 'creds' && (
                        <div className="mt-6 card-soft p-5 space-y-3">
                            <Input label="Email" type="email" value={email} onChange={setEmail} tid="sa-email" />
                            <Input label="Password" type="password" value={password} onChange={setPassword} tid="sa-password" />
                            {error && <div className="text-sm text-red-700 rounded bg-red-50 border border-red-200 px-3 py-2" data-testid="sa-login-error">{error}</div>}
                            <button onClick={step1} disabled={busy} className="btn-red-lg" data-testid="sa-login-btn">{busy ? 'Signing in\u2026' : 'Sign In'}</button>
                        </div>
                    )}

                    {step === 'totp' && (
                        <div className="mt-6 card-soft p-5 space-y-3">
                            <div className="text-sdf-red font-bold text-xs tracking-widest">TWO-FACTOR AUTHENTICATION</div>
                            <p className="text-xs text-sdf-text-2">Enter the 6-digit code from Google Authenticator.</p>
                            <Input label="Authenticator code" value={totpCode} onChange={(v) => setTotpCode(v.replace(/\D/g, ''))} tid="sa-totp" />
                            {error && <div className="text-sm text-red-700 rounded bg-red-50 border border-red-200 px-3 py-2">{error}</div>}
                            <button onClick={step2} disabled={busy} className="btn-red-lg" data-testid="sa-verify-totp">{busy ? 'Verifying\u2026' : 'Verify & Sign In'}</button>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
function Input({ label, type = 'text', value, onChange, tid }) {
    return (
        <div>
            <label className="text-[10px] font-bold text-sdf-text-2 tracking-widest">{label.toUpperCase()}</label>
            <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full border border-sdf-line rounded-xl px-3 py-3 outline-none text-sm bg-white" data-testid={tid} />
        </div>
    );
}
